import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Select, MenuItem,
  IconButton, Dialog, DialogActions, DialogContent, Grid, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  useMediaQuery, useTheme, Chip
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../api/axiosConfig';

/* ---------- ligne de tag ---------- */
const TagRow = ({ tag, onEdit, onDelete, groupes, isSmallScreen, t }) => {
  // Récupérer les noms des groupes associés
  const getGroupeNames = () => {
    if (!tag.groupes || tag.groupes.length === 0) return t('canteraTags.noGroup'); 
    return tag.groupes
      .map(gId => {
        const groupe = groupes.find(g => g.id === gId);
        return groupe ? groupe.libelle : '';
      })
      .filter(Boolean)
      .join(', ');
  };

  return (
    <TableRow hover>
      <TableCell>
        <Chip
          label={tag.libelle}
          sx={{ backgroundColor: '#2196F3', color: '#fff' }}
        />
      </TableCell>
      <TableCell>{getGroupeNames()}</TableCell>
      <TableCell align="right">
        <IconButton onClick={() => onEdit(tag)} sx={{ color: '#ff9800' }}>
          <Edit />
        </IconButton>
        <IconButton onClick={() => onDelete(tag)} sx={{ color: 'red' }}>
          <Delete />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

/* ---------- composant principal ---------- */
const CanteraTags = () => {
  const { t } = useTranslation();
  const [tagName, setTagName] = useState('');
  const [tagDescription, setTagDescription] = useState('');
  const [selectedGroupes, setSelectedGroupes] = useState([]);
  const [tags, setTags] = useState([]);
  const [groupes, setGroupes] = useState([]);
  const [editTag, setEditTag] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const section = process.env.REACT_APP_SECTION;

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  /* ----------- helpers ----------- */

  const fetchTags = async () => {
    try {
      const { data } = await api.get('/api/cantera-tags');
      setTags(data);
    } catch (err) {
      console.error('Error al recuperar los tags cantera:', err);
    }
  };

  const fetchGroupes = async () => {
    try {
      const { data } = await api.get('/api/groups');
      // Filtrer uniquement les groupes cantera
      const canteraGroupes = data.filter(g => g.cantera === true || g.cantera === 1);
      setGroupes(canteraGroupes);
    } catch (err) {
      console.error('Error al recuperar los grupos:', err);
    }
  };

  /* ----------- chargement initial ----------- */
  useEffect(() => {
    fetchTags();
    fetchGroupes();
  }, []);

  /* ----------- création ----------- */
  const handleAddTag = async () => {
    if (!tagName) {
      alert(t('canteraTags.enterTagName'));
      return;
    }
    if (selectedGroupes.length === 0) {
      alert(t('canteraTags.selectAtLeastOneGroup'));
      return;
    }
    try {
      const { data } = await api.post('/api/cantera-tags', {
        libelle: tagName,
        description: tagDescription,
        groupes: selectedGroupes
      });
      setTags([...tags, data]);
      setTagName('');
      setTagDescription('');
      setSelectedGroupes([]);
    } catch (err) {
      console.error('Error al añadir el tag cantera:', err);
    }
  };

  /* ----------- édition ----------- */
  const handleEditTag = (tag) => {
    setEditTag(tag);
    setTagName(tag.libelle);
    setTagDescription(tag.description || '');
    setSelectedGroupes(tag.groupes || []);
  };

  const handleSaveEdit = async () => {
    if (!tagName) {
      alert(t('canteraTags.enterTagName'));
      return;
    }
    if (selectedGroupes.length === 0) {
      alert(t('canteraTags.selectAtLeastOneGroup'));
      return;
    }
    try {
      await api.put(`/api/cantera-tags/${editTag.id}`, {
        libelle: tagName,
        description: tagDescription,
        groupes: selectedGroupes
      });
      const updated = tags.map(t =>
        t.id === editTag.id
          ? { ...t, libelle: tagName, description: tagDescription, groupes: selectedGroupes }
          : t
      );
      setTags(updated);
      setTagName('');
      setTagDescription('');
      setSelectedGroupes([]);
      setEditTag(null);
    } catch (err) {
      console.error('Error al actualizar el tag cantera:', err);
    }
  };

  /* ----------- suppression ----------- */
  const handleDeleteTag = async (tag) => {
    try {
      await api.delete(`/api/cantera-tags/${tag.id}`);
      setTags(tags.filter(t => t.id !== tag.id));
      setConfirmDelete(null);
    } catch (err) {
      console.error('Error al eliminar el tag cantera:', err);
    }
  };

  /* ----------- rendu ----------- */
  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" sx={{ mb: 4 }}>{t('canteraTags.title')}</Typography>

      {/* ------ formulaire de création / édition ------ */}
      <Grid container spacing={2} direction={isSmallScreen ? 'column' : 'row'} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t('canteraTags.tagName')}
            variant="outlined"
            value={tagName}
            onChange={(e) => setTagName(e.target.value)}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            label={t('canteraTags.descriptionOptional')}
            variant="outlined"
            value={tagDescription}
            onChange={(e) => setTagDescription(e.target.value)}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <Select
            multiple
            value={selectedGroupes}
            onChange={(e) => {
              const value = e.target.value;
              // Si "tous" est sélectionné
              if (value.includes('tous')) {
                // Si tous les groupes sont déjà sélectionnés, on désélectionne tout
                if (selectedGroupes.length === groupes.length) {
                  setSelectedGroupes([]);
                } else {
                  // Sinon on sélectionne tous les groupes
                  setSelectedGroupes(groupes.map(g => g.id));
                }
              } else {
                setSelectedGroupes(value);
              }
            }}
            displayEmpty
            variant="outlined"
            fullWidth
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <em>{t('canteraTags.selectGroups')}</em>;
              }
              if (selected.length === groupes.length) {
                return t('canteraTags.allGroups');
              }
              return selected
                .map(gId => {
                  const groupe = groupes.find(g => g.id === gId);
                  return groupe ? groupe.libelle : '';
                })
                .filter(Boolean)
                .join(', ');
            }}
          >
            <MenuItem value="tous">
              <Box sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', color: 'primary.main' }}>
                {selectedGroupes.length === groupes.length ? '✓ ' : ''}
                {t('canteraTags.selectAll')}
              </Box>
            </MenuItem>
            <MenuItem disabled sx={{ borderBottom: '1px solid #e0e0e0', pb: 0.5, mb: 0.5 }}>
              <em style={{ fontSize: '0.85rem' }}>{t('canteraTags.groups')}:</em>
            </MenuItem>
            {groupes.map((groupe) => (
              <MenuItem key={groupe.id} value={groupe.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: groupe.code_couleur || '#2196F3',
                    }}
                  />
                  {groupe.libelle}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={12} sm={12}>
          <Button
            variant="contained"
            onClick={editTag ? handleSaveEdit : handleAddTag}
            disabled={!tagName || selectedGroupes.length === 0}
            sx={{
              backgroundColor: '#ff9800',
              color: '#fff',
              height: '56px',
              width: '180px',
              '&.Mui-disabled': {
                backgroundColor: '#ccc',
                color: '#888'
              }
            }}
          >
            {editTag ? t('canteraTags.saveChanges') : t('canteraTags.addTag')}
          </Button>
        </Grid>
      </Grid>

      {/* ------ tableau des tags ------ */}
      <Typography variant="h6" sx={{ mb: 2 }}>{t('canteraTags.tagList')}:</Typography>

      <TableContainer component={Paper}>
        <Table size={isSmallScreen ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              <TableCell>{t('canteraTags.tag')}</TableCell>
              <TableCell>{t('canteraTags.groups')}</TableCell>
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {tags.map((tag) => (
              <TagRow
                key={tag.id}
                tag={tag}
                onEdit={handleEditTag}
                onDelete={setConfirmDelete}
                groupes={groupes}
                isSmallScreen={isSmallScreen}
                t={t}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ------ boîte de confirmation suppression ------ */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogContent>
          <Typography>{t('canteraTags.confirmDelete', { name: confirmDelete?.libelle })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} sx={{ color: 'grey' }}>{t('common.cancel')}</Button>
          <Button onClick={() => handleDeleteTag(confirmDelete)} sx={{ color: 'red' }}>{t('common.delete')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CanteraTags;
